import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { CheckBox, NewInput, NewInputList, NewSelect, NewSelectList } from "../.."
import { ButtonTypes, ListStyle_Types, NewInputHandles, SelectFieldOption_Values } from "../../../Definitions";
import { getBorderRadius } from "../../_Components";
import { DirectionOptions, NumberingOptions, PatternOptions, Pen, useOptionTranslate, StartCornerOptions, Station } from "../../../Definitions/newSectionModalInterfaces";
import { useModalContext } from "../../LocationFeeder/Modals/_Modals/newSectionModalContext";
import { generateNumberedGrid } from "../../LocationFeeder/Modals/_Modals/generateNumberedGrid";
import Counter from "../../BaseComponents/counter";
import NewButton from "../../BaseComponents/newButton";

// TODO

interface CheckedContent {
  feeder: number,
  water: boolean,
  button: boolean,
}

const NewEsfWfSectionModalLvl1: React.FC = () => {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const { state: { farm, domain, connection, error, section }, dispatch } = useModalContext()
  const [ErrorText, setErrorText] = useState<string | undefined>(undefined);
  // const [startingNumber, setStartingNumber] = useState<number>(101);
  const nameRef = React.useRef<NewInputHandles>(null); 

  const startCornerOptions = useOptionTranslate()

  const stations = { ...section, contents: section.contents as Station[] };

  // Error handling
  useEffect(() => {
    if (!error) return;
    if (!error.errorText) setErrorText(undefined);
    else setErrorText(error.errorText);

    nameRef.current?.setError(error.Elementid === 1)
  }, [error])
  
  // Handle station update when numbering is automatic
  useEffect(() => {
    if (section.numbering === NumberingOptions.automatic && section.startCorner && section.direction && section.pattern && section.startNumber) {
      const autoStations = generateNumberedGrid(
        section.rows,
        section.columns,
        section.startCorner,
        section.direction,
        section.pattern,
        section.startNumber
      );
      dispatch({section:{contents: autoStations}})
    }
  }, [section.numbering, section.rows, section.columns, section.startCorner, section.direction, section.pattern, section.startNumber, dispatch]);

  // #region | Handle Input Fields
  const handleRowsChange = (e: any) => {
    dispatch({section:{rows:e}})
    dispatch({section:{contents:[]}})
  };

  const handleStartNumberChange = (e: any) => {
    dispatch({section:{startNumber:e}})
  };
  // #endregion

  // #region | Tableview functionality
  const totalCells = section.rows * section.columns;

  const handleCellClick = (stationId: number) => {
    const stationIdStr = String(stationId);
    const existingIndex = stations.contents.findIndex(s => s.stationId === stationIdStr);
  
    if (existingIndex !== -1) {
      const stationToRemove = section.contents[existingIndex];
      const removedNameNum = parseInt(stationToRemove.name ?? "", 10);
  
      let updated = stations.contents.filter(s => s.stationId !== stationIdStr);
  
      updated = updated.map(station => {
        const nameNum = parseInt(station.name ?? "", 10);
        if (nameNum > removedNameNum) {
          return { ...station, name: String(nameNum - 1) };
        }
        return station;
      });
      dispatch({section:{contents:updated}})
  
      dispatch({section:{numbering:NumberingOptions.manual}})
  
    } else {
      const existingNames = stations.contents.map(s => parseInt(s.name ?? "", 10)).filter(n => !isNaN(n));
      const maxName = existingNames.length ? Math.max(...existingNames) : (section.startNumber ?? 0) - 1;
      const newName = maxName + 1;
      const rowStart = Math.floor(stationId / section.columns) + 1;
      const colStart = (stationId % section.columns) + 1;
      const rowEnd = rowStart + 1;
      const colEnd = colStart + 1;
      const visual = `${rowStart}/${colStart}/${rowEnd}/${colEnd}`;
      // const visual = JSON.parse(JSON.stringify(`${rowStart}/${colStart}/${rowEnd}/${colEnd}`));
      const newStation: Station = { stationId: stationIdStr, visual, io: null, id: "", name: String(newName) };
      
      dispatch({section:{contents:[...section.contents, newStation]}})
    }
  };
  
  const getCellNumber = (stationId: number) => {
    const index = stations.contents.findIndex(s => s.stationId === String(stationId));
  
    if (index !== -1) {
      const startNum = Number(section.startNumber ?? 0); 
      const calculatedNumber = startNum + index;
      return String(calculatedNumber);
    }
    return null;
  };
  // #endregion

  // #region | TableView
  const tableView = (
    Array.from({ length: totalCells }).map((_, i) => {
      const isSelected = stations.contents.some(s => s.stationId === String(i));
      const number = getCellNumber(i);

      const row = Math.floor(i / section.columns);
      const col = i % section.columns;
      const borderRadius = getBorderRadius({ row, col, columns: section.columns, rows: section.rows });

      return (
        <div
          key={i}
          onClick={() => handleCellClick(i)}
          style={{
            textAlign: "center",
            alignContent: "center",
            backgroundColor: colors.greyLightSecondary,
            filter: isSelected ? "opacity(1)" : "opacity(0.5)",
            color: colors.greyDarkPrimary,
            ...fonts.paragraph,
            ...fonts.fontSize4,
            cursor: "pointer",
            borderRadius
          }}
        >
          {number ?? ""}
        </div>
      );
    })
  );
  // #endregion

  // #region | Handle Checkboxes
  const handleCheckboxChange = (name: keyof CheckedContent) => {
    if (name === 'feeder') {
      dispatch({
        section: {
          feeder: 1,
          water: false,
          button: false,
        },
      });
    } else {
      dispatch({
        section: {
          feeder: 0,
          [name]: !section[name],
        },
      });
    }
  };
  // #endregion
  
  return (
    <div style={{ width: "100%", borderTop: "2px solid " + colors.blueDarkSecondary, margin: "20px 0px" }}>
      
      <div style={{marginBottom: mobileXs ? "20px" : undefined, padding: "10px 10px", maxHeight: "40vh", overflowY: "auto", overflowX: "hidden", scrollbarWidth: "thin", scrollbarColor: colors.blueDarkSecondary+colors.transparent, display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "350px 1fr", gridTemplateRows: mobileXs ? "250px 1fr" : "1fr" }}>
        <div style={{ gridArea: "1/1/2/2", display: "flex", flexDirection: "column", gap: "15px", alignItems: mobileXs ? "center" : undefined }}>
          {/* Section Name */}
          <NewInput
            ref={nameRef}
            placeholder=""
            header={t("locationFeederPages.sections.sectionName", { defaultValue: "Section Name" })}
            isRequired
            onChange={(e) => dispatch({ section: { name: e } })}
            valueOverrides={{
              defaultValue: section.name
            }}
          />
          {ErrorText && (
            <p style={{ color: colors.systemRed, ...fonts.paragraph, ...fonts.fontSize4 }}>
              {ErrorText}
            </p>
          )}

          {/* Columns, Rows, First Station No. */}
          <NewInputList
            placeholders={["number", "number"]}
            headers={[t("devices.rows", {defaultValue: "Rows"}), "First station No."]}
            onChanges={[handleRowsChange, handleStartNumberChange]}
            valueOverrides={{
              defaultValues: [section.rows, section.startNumber ?? 0],
              valueTypes: ["number", "number"],
              maxValueNumbers: [undefined, undefined],
              minValueNumbers: [1, 1]
            }}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "15px"
            }}
          />
        </div>

        {/* Table View */}
        <div style={{ gridArea: mobileXs ? "2/1/3/2" : "1/2/2/3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: "5px"}}>
          <div style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "5px"}}>         
            <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{`Number of Stations:`}</p>
            <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary }}>{section.contents.length}</p>
          </div>
          <div style={{
            display: "grid",
            gridTemplateRows: `repeat(${section.rows}, 30px)`,
            gridTemplateColumns: `repeat(${section.columns}, 130px)`,
            gap: "10px",
          }}>
            {tableView}
            </div>
          <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, textAlign: "center", whiteSpace: 'pre-wrap', maxWidth: "150px"}}>{"Click on a station to remove it or add it again."}</p>
        </div>

        {/* Reset Btn */}
        <div style={{position: "absolute", top: "185px", right: "45px"}}>
          <NewButton
            content={`${t("reset", {defaultValue: "reset"})}`}
            buttonType={ButtonTypes.textButton}
            endEnhancer={glyphs.ArrowReset({ size: "15px"})}
            onClick={() => dispatch({section:{contents:[]}})}
          />
        </div> 
      </div>

      {/* Station Content */}
      <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{"Station Content"}</p>
      <div style={{borderTop: "2px solid "+colors.blueDarkSecondary, borderBottom: "2px solid "+colors.blueDarkSecondary, padding: "10px 0px", maxHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{width: "fit-content", display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "2fr 1fr 1fr", gridTemplateRows: mobileXs ? "1fr 1fr 1fr" : "1fr", columnGap: "100px", rowGap: "10px"}}>
          <Counter text={"Number of feeders"} valueOverrides={{minValueNumber: 1, maxValueNumber: 1, defaultValue: 1}} isDisabled/>
          <CheckBox text={t("locationFeederPages.sections.water", {defaultValue: "Water"})} isChecked={section.water} onChange={() => handleCheckboxChange("water")}/> 
          <CheckBox text={"Button"} isChecked={section.button} onChange={() => (handleCheckboxChange("button"))}/> 
        </div>
      </div>

    </div>
  );
}

export default NewEsfWfSectionModalLvl1


